import { useState } from "react";
import { ApiError, api, type SelfUser } from "@shared/api";
import { Mark, Tile, fromSelf, markOf } from "@entities/user";
import { time } from "@shared/lib/format";
import { bannerTint } from "@shared/lib/tint";
import { Dialog as DialogRoot, DialogContent, DialogTitle } from "@shared/ui/dialog";
import { EmojiPick } from "@shared/ui/EmojiPick";
import { FileImage } from "@shared/ui/FileImage";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { IconCheck, IconChevronDown, IconClose, IconEmoji } from "@shared/ui/icons";
import { DEFAULT_PROMPT } from "../lib/prompts";

const TEXT_MAX = 128;
const HOUR = 3_600_000;

/** «Удалить через» — сроки Discord; «24 ч.» у него и есть «сегодня». */
const AFTER = [
  { value: "24h", ms: 24 * HOUR, short: "24 ч." },
  { value: "4h", ms: 4 * HOUR, short: "4 ч." },
  { value: "1h", ms: HOUR, short: "1 ч." },
  { value: "30m", ms: HOUR / 2, short: "30 мин." },
  { value: "never", ms: null, short: "Не удалять" },
] as const;

type After = (typeof AFTER)[number]["value"];

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** Когда снимется: «17:51» сегодня, «завтра в 17:51» — позже. */
function when(ms: number, now = new Date()): string {
  const at = new Date(now.getTime() + ms);
  return sameDay(now, at) ? time(at) : `завтра в ${time(at)}`;
}

/** Подпись выбора, как у Discord: «Удалить в 21:51», «Удалить завтра в 17:51», «Не удалять». */
function summary(after: After): string {
  const item = AFTER.find((entry) => entry.value === after)!;
  if (item.ms === null) return item.short;
  const at = new Date(Date.now() + item.ms);
  return sameDay(new Date(), at) ? `Удалить в ${time(at)}` : `Удалить завтра в ${time(at)}`;
}

/**
 * Какой срок выбран, когда окно открылось, — правило Discord: статуса нет —
 * 24 ч.; есть без срока — «Не удалять»; срок не сегодня — 24 ч.; сегодня —
 * ближайший из 30 мин., 1 ч. и 4 ч., что не короче оставшегося.
 */
function initial(me: SelfUser): After {
  if (!(me.status_text || me.status_emoji)) return "24h";
  if (!me.status_expires) return "never";
  const at = new Date(me.status_expires);
  if (!sameDay(new Date(), at)) return "24h";
  const left = at.getTime() - Date.now();
  const limits = { "30m": HOUR / 2, "1h": HOUR, "4h": 4 * HOUR } as const;
  return (["30m", "1h", "4h"] as const).find((value) => left <= limits[value]) ?? "24h";
}

/**
 * Свой статус — как «Настроить статус» в Discord: сверху своя карточка с
 * пузырём (видно, как статус будет выглядеть), ниже поле со знаком и
 * крестиком, внизу срок «Удалить через» и «Сохранить». Пустое поле при
 * сохранении снимает статус. Материал — «Почты» (`surface-pop`), не стекло.
 */
export function StatusDialog({
  me,
  prompt = DEFAULT_PROMPT,
  onSaved,
  onClose,
}: {
  me: SelfUser;
  /** Подсказка в поле — та же, что была в пузыре своей карточки. */
  prompt?: string;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [emoji, setEmoji] = useState<string | null>(me.status_emoji ?? null);
  const [text, setText] = useState(me.status_text ?? "");
  const [after, setAfter] = useState<After>(() => initial(me));
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const person = fromSelf(me);
  const filled = !!(text.trim() || emoji);

  async function save() {
    setBusy(true);
    setProblem(null);
    const ms = AFTER.find((entry) => entry.value === after)!.ms;
    try {
      await api.users.updateProfile(
        filled
          ? {
              status_text: text.trim() || null,
              status_emoji: emoji,
              status_expires: ms === null ? null : new Date(Date.now() + ms).toISOString(),
            }
          : { status_text: null, status_emoji: null, status_expires: null },
      );
      onSaved();
      onClose();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Не сохранилось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogRoot open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="status-modal surface-pop" aria-describedby={undefined}>
        <div className="status-modal-head">
          <DialogTitle className="status-modal-title">Настроить статус</DialogTitle>
          <button type="button" className="profile-icon-btn status-modal-close" aria-label="Закрыть" onClick={onClose}>
            <IconClose size={16} />
          </button>
        </div>

        {/* Как будет выглядеть: своя карточка с пузырём, как в Discord. */}
        <div className="status-preview profile-pop" aria-hidden inert>
          <div className="profile-pop-scroll">
            <div
              className={`profile-head-banner ${me.banner_id ? "image" : ""}`}
              style={{ background: me.banner_color ?? bannerTint(me.handle) }}
            >
              {me.banner_id && <FileImage id={me.banner_id} alt="" />}
            </div>
            <span className="profile-av marked">
              <Tile person={person} size={80} mark={null} />
              <span className="profile-av-mark">
                <Mark kind={markOf(me.status_presence ?? "online")} size={16} />
              </span>
            </span>
            <span className={`self-pop-bubble ${filled ? "" : "prompt"}`}>
              {emoji && <span className="status-mark">{emoji}</span>}
              <span className="self-pop-bubble-text">{text.trim() || (emoji ? "" : prompt)}</span>
            </span>
            <div className="profile-pop-body">
              <div>
                <div className="profile-title">
                  <span className="ellipsis">{person.name}</span>
                </div>
                <div className="profile-sub">
                  <span className="ellipsis">{me.handle}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <label className="status-modal-label" htmlFor="custom-status-input">
          Статус
        </label>
        <div className="status-field">
          <EmojiPick
            align="start"
            onPick={setEmoji}
            trigger={
              <button
                type="button"
                className="status-field-emoji"
                aria-label={emoji ? `Знак ${emoji}, сменить` : "Знак статуса не выбран"}
              >
                {emoji ?? <IconEmoji size={20} />}
              </button>
            }
          />
          <input
            id="custom-status-input"
            className="status-field-input"
            value={text}
            maxLength={TEXT_MAX}
            autoFocus
            placeholder={prompt}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void save();
              }
            }}
          />
          {filled && (
            <button
              type="button"
              className="status-field-clear"
              aria-label="Удалить пользовательский статус"
              onClick={() => {
                setText("");
                setEmoji(null);
              }}
            >
              <IconClose size={14} />
            </button>
          )}
        </div>
        {problem && <div className="small danger-text">{problem}</div>}

        <div className="status-modal-foot">
          <Popover open={picking} onOpenChange={setPicking}>
            <PopoverTrigger asChild>
              <button type="button" className="status-after" aria-label={`Удалить через: ${summary(after)}`}>
                {summary(after)}
                <IconChevronDown size={14} />
              </button>
            </PopoverTrigger>
            <PopoverContent side="right" align="start" sideOffset={8} aria-label="Удалить через">
              <div className="status-after-list surface-pop" role="listbox" aria-label="Удалить через">
                <div className="status-after-head">Удалить через</div>
                {AFTER.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    role="option"
                    aria-selected={after === item.value}
                    className={`status-after-item ${after === item.value ? "on" : ""}`}
                    onClick={() => {
                      setAfter(item.value);
                      setPicking(false);
                    }}
                  >
                    <span className="grow">{item.ms === null ? item.short : `${item.short} (${when(item.ms)})`}</span>
                    {after === item.value && <IconCheck size={14} />}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {/* Капсула CSS, а не капля движка: на материале без стекла тень капли рисует коробку. */}
          <button type="button" className="profile-cta" disabled={busy} onClick={() => void save()}>
            Сохранить
          </button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
